import type { ReactNode } from 'react'
import { Modal, Button, Tag, Tabs, Table } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { InfoCircleOutlined } from '@ant-design/icons'
import type { SupplementTab } from './SupplementMaterialsModal'
import { parseRowAttachments, AttachmentItemLinks } from './relationAttachments'
import { formatDisplayDateTime } from '../../utils/format'
import { formatDocumentStatus } from '../../utils/documentStatus'
import { KINGDEE_TAB_LINE_ITEMS_KEY, KINGDEE_TAB_PLAN_ITEMS_KEY } from '../../services/api'
import {
  pickRowValue,
  type FieldDef,
  type TableColDef,
  CONTRACT_BASIC_FIELDS,
  CONTRACT_FINANCE_FIELDS,
  CONTRACT_LINE_COLUMNS,
  ORDER_BASIC_FIELDS,
  ORDER_FINANCE_FIELDS,
  ORDER_LINE_COLUMNS,
  RECEIPT_BASIC_FIELDS,
  RECEIPT_LINE_COLUMNS,
  LOGISTICS_BASIC_FIELDS,
  LOGISTICS_LINE_COLUMNS,
  LOGISTICS_MATERIAL_GROUPS,
  INVOICE_BASIC_FIELDS,
  INVOICE_LINE_COLUMNS,
  INVOICE_PLAN_COLUMNS,
} from './relationDetailModalSpec'

type Props = {
  open: boolean
  tab: SupplementTab | null
  row: Record<string, unknown> | null
  onClose: () => void
}

function fmtMoney(n: unknown) {
  if (n === '' || n === undefined || n === null) return '-'
  const x = Number(n)
  if (Number.isNaN(x)) return '-'
  return `¥${x.toLocaleString()}`
}

function formatScalar(v: unknown): ReactNode {
  if (v == null) return '-'
  if (typeof v === 'string' && v.trim() === '') return '-'
  if (typeof v === 'boolean') return v ? '是' : '否'
  if (typeof v === 'object') {
    try {
      return (
        <pre className="text-[12px] whitespace-pre-wrap break-all m-0 max-h-48 overflow-auto bg-gray-50 dark:bg-gray-800 p-2 rounded">
          {JSON.stringify(v, null, 2)}
        </pre>
      )
    } catch {
      return String(v)
    }
  }
  const s = String(v).trim()
  return s === '' ? '-' : s
}

function normalizeDetail(node: ReactNode): ReactNode {
  if (node == null || node === false) return '-'
  if (typeof node === 'string' || typeof node === 'number') {
    const t = String(node).trim()
    return t === '' ? '-' : node
  }
  return node
}

function DetailField({
  label,
  children,
  fullWidth,
  colSpan = 2,
}: {
  label: string
  children: ReactNode
  fullWidth?: boolean
  colSpan?: 2 | 3
}) {
  const spanClass = fullWidth ? (colSpan === 3 ? 'sm:col-span-3' : 'sm:col-span-2') : ''
  return (
    <div className={`${spanClass} text-[12px] leading-snug`}>
      <div className="flex flex-row flex-wrap items-baseline gap-x-2 gap-y-0.5 min-w-0">
        <span className="text-gray-500 shrink-0 whitespace-nowrap">{label}：</span>
        <div className="text-[12px] text-black min-w-0 flex-1 break-words [&_.ant-tag]:text-[12px] [&_.ant-tag]:leading-tight [&_a]:text-[12px] [&_button]:!text-[12px] [&_.ant-btn]:!h-auto [&_.ant-btn]:!py-0">
          {children}
        </div>
      </div>
    </div>
  )
}

function pickHeadline(...vals: unknown[]) {
  for (const v of vals) {
    if (v != null && String(v).trim() !== '') return String(v).trim()
  }
  return ''
}

/** 物流 Tab「单据类型」列与列表 render 对齐（含 formId 兜底文案） */
function logisticsDocumentTypeLabel(row: Record<string, unknown>): string {
  const name = pickHeadline(
    row.bill_type_id_name,
    row.bill_type_id_fname,
    row.billTypeName,
    row.bill_type_name,
  )
  if (name) return name
  const fid = row.formId
  if (fid === 'SAL_DELIVERYNOTICE') return '发货通知'
  if (fid === 'SAL_OUTSTOCK') return '销售出库'
  return ''
}

/** 发票 Tab「类型」列与 index 表 render 一致 */
function invoiceTypeColumnText(row: Record<string, unknown>): string {
  const t = String(row.type ?? row.bill_type_id_name ?? row.bill_type_id_fname ?? '').trim()
  return t === '-' ? '' : t
}

/** 发票 Tab「单据类型」：仅金蝶单据类型字段 */
function invoiceDocumentTypeLabel(row: Record<string, unknown>): string {
  return pickHeadline(
    row.bill_type_id_name,
    row.bill_type_id_fname,
    row.billTypeName,
    row.bill_type_name,
  )
}

/** 弹框标题：优先「单据类型 · 类型」；缺省或相同时去重；再退回单号等 */
function joinDocTypeAndTypeTitle(
  documentType: string,
  typeKind: string,
  fallbacks: unknown[],
  defaultLabel: string,
): string {
  const a = (documentType || '').trim()
  const b = (typeKind || '').trim()
  if (a && b && a !== b) return `${a} · ${b}`
  if (a && b) return a
  const primary = a || b
  if (primary) return primary
  return pickHeadline(...fallbacks) || defaultLabel
}

function headlineForRow(tab: SupplementTab, row: Record<string, unknown>): string {
  switch (tab) {
    case 'contract':
      return pickHeadline(row.name, row.no, row.contractName) || '合同'
    case 'order':
      return pickHeadline(row.title, row.no) || '订单'
    case 'firstPayment':
      return pickHeadline(row.no) || '收款'
    case 'logistics':
      return joinDocTypeAndTypeTitle(
        logisticsDocumentTypeLabel(row),
        pickHeadline(
          row.type,
          row.document_type,
          row.biz_type,
          row.business_type,
        ),
        [
          row.bill_no,
          row.billNo,
          row.trackingNo,
          row.tracking_no,
          row.carriage_no,
          row.carrier,
        ],
        '物流',
      )
    case 'invoice':
      return joinDocTypeAndTypeTitle(
        invoiceDocumentTypeLabel(row),
        invoiceTypeColumnText(row),
        [row.no, row.bill_no, row.invoiceNo, row.invoice_no],
        '发票',
      )
    default:
      return '详情'
  }
}

function decorateValue(
  label: string,
  node: ReactNode,
  options?: { plain?: boolean },
): ReactNode {
  const plain = Boolean(options?.plain)
  const moneyLike =
    label !== '汇率类型' &&
    /金额|价值|价税|税额|单价|合计|收款|付款|实收|毛利|保证金|本位币|汇率|折扣|退款|比例/.test(label)
  const wrapCls = moneyLike ? 'font-semibold text-gray-900' : ''

  if (typeof node !== 'string' && typeof node !== 'number') {
    if (plain) {
      return <span className="text-[12px] text-black">{node}</span>
    }
    return moneyLike ? <span className={wrapCls}>{node}</span> : node
  }
  const text = String(node).trim()
  if (text === '' || text === '-') return '-'

  if (!plain) {
    const typeAsTag =
      label === '类型' ||
      label === '单据类型' ||
      (label.includes('类型') && !label.includes('类型编码') && !label.includes('汇率类型'))
    if (typeAsTag) {
      return (
        <Tag color="blue" bordered className="m-0 align-middle text-[12px] leading-tight h-auto py-0 px-1.5">
          {text}
        </Tag>
      )
    }

    if (label === '单据状态' || label === '合同标识') {
      return (
        <Tag color="blue" bordered className="m-0 align-middle text-[12px] leading-tight h-auto py-0 px-1.5">
          {text}
        </Tag>
      )
    }
  }

  if (plain) {
    return <span className="text-[12px] text-black">{text}</span>
  }
  return moneyLike ? <span className={wrapCls}>{text}</span> : text
}

const SKIP_KEYS = new Set([
  'key',
  '_logisticsDocSource',
  KINGDEE_TAB_LINE_ITEMS_KEY,
  KINGDEE_TAB_PLAN_ITEMS_KEY,
  '_attachments',
  'attachments',
  'attachmentList',
  'attachment_list',
  'supplementAttachments',
  'supplement_attachments',
  'files',
  'appendixList',
  'appendix_list',
])

function isPlainObjectRecordArray(arr: unknown): arr is Record<string, unknown>[] {
  return (
    Array.isArray(arr) &&
    arr.length > 0 &&
    arr.every((x) => x != null && typeof x === 'object' && !Array.isArray(x))
  )
}

const ARRAY_SKIP_KEY_RE = /attachment|file|appendix|附件/i

function objectArrayKeys(row: Record<string, unknown>): string[] {
  return Object.keys(row).filter((k) => {
    if (SKIP_KEYS.has(k)) return false
    if (ARRAY_SKIP_KEY_RE.test(k)) return false
    return isPlainObjectRecordArray(row[k])
  })
}

const DETAIL_KEY_RE =
  /entry|detail|line|item|entity|分录|明细|fbillentry|billentry|saleorderentry|receivebillentry|contractentry|outstockentry|deliveryentry|invoiceentry|fentity/i

const PLAN_KEY_RE = /recplan|receiv.*plan|收款计划|payplan|freceiptplan|rec_plan|invoiceplan|sal_invoice.*plan|receipt_plan|planentity|invoicerecplan/i

const MATERIAL_KEY_RE = /material|物料|商品|materialentity|stock|inventory|productlist|materialdetail/i

function pickDetailSourceKey(row: Record<string, unknown>, tab: SupplementTab): string | null {
  const keys = objectArrayKeys(row).filter((k) => !(tab === 'invoice' && PLAN_KEY_RE.test(k)))
  if (!keys.length) return null
  const detailCandidates = keys.filter((k) => DETAIL_KEY_RE.test(k))
  if (detailCandidates.length) {
    return detailCandidates.sort((a, b) => (row[b] as unknown[]).length - (row[a] as unknown[]).length)[0]
  }
  const nonPlan = keys.filter((k) => !(tab === 'invoice' && PLAN_KEY_RE.test(k)))
  if (!nonPlan.length) return null
  let best = nonPlan[0]
  let bestLen = (row[best] as unknown[]).length
  for (const k of nonPlan.slice(1)) {
    const len = (row[k] as unknown[]).length
    if (len > bestLen) {
      best = k
      bestLen = len
    }
  }
  return best
}

function pickPlanSourceKey(row: Record<string, unknown>): string | null {
  const keys = objectArrayKeys(row).filter((k) => PLAN_KEY_RE.test(k))
  if (keys.length) return keys[0]
  return null
}

/** 金蝶 Tab 接口：`data.items` 经 `unwrapKingdeeTabRows` 挂在行 `_kingdeeTabLineItems`；否则回退从行内猜分录数组 */
function resolveDetailLineRows(
  row: Record<string, unknown>,
  tab: SupplementTab,
): Record<string, unknown>[] | null {
  const fromTab = row[KINGDEE_TAB_LINE_ITEMS_KEY]
  if (Array.isArray(fromTab)) return fromTab as Record<string, unknown>[]
  const detailKey = pickDetailSourceKey(row, tab)
  if (!detailKey) return null
  const arr = row[detailKey]
  return Array.isArray(arr) ? (arr as Record<string, unknown>[]) : null
}

function resolvePlanRows(row: Record<string, unknown> | null): Record<string, unknown>[] | null {
  if (!row) return null
  const pinned = row[KINGDEE_TAB_PLAN_ITEMS_KEY]
  if (Array.isArray(pinned)) return pinned as Record<string, unknown>[]
  const planKey = pickPlanSourceKey(row)
  if (!planKey) return null
  const arr = row[planKey]
  return Array.isArray(arr) ? (arr as Record<string, unknown>[]) : null
}

function rowHasLogisticsMaterialFields(r: Record<string, unknown>): boolean {
  const code = pickRowValue(r, ['material_id_number', 'material_number', 'fmaterialid_number'])
  const name = pickRowValue(r, ['material_id_name', 'material_name', 'material_id_fname'])
  return (
    (code != null && String(code).trim() !== '') ||
    (name != null && String(name).trim() !== '')
  )
}

/** 出库分录首行；若无分录则从发货通知/出库单头扁平字段取物料 */
function resolveLogisticsMaterialLineRow(
  row: Record<string, unknown>,
  firstLine: Record<string, unknown> | null,
  materialKey: string | null,
): Record<string, unknown> {
  if (materialKey) {
    const arr = row[materialKey]
    if (Array.isArray(arr) && arr.length > 0) {
      const x = arr[0]
      if (x && typeof x === 'object' && !Array.isArray(x)) return x as Record<string, unknown>
    }
  }
  if (firstLine && rowHasLogisticsMaterialFields(firstLine)) return firstLine
  if (rowHasLogisticsMaterialFields(row)) return row
  return firstLine ?? {}
}

function pickMaterialSourceKey(row: Record<string, unknown>, detailKey: string | null): string | null {
  const keys = objectArrayKeys(row)
  const matOnly = keys.filter((k) => MATERIAL_KEY_RE.test(k) && k !== detailKey)
  if (matOnly.length) return matOnly[0]
  const other = keys.find((k) => k !== detailKey && !PLAN_KEY_RE.test(k))
  return other ?? null
}

function formatFieldCell(label: string, raw: unknown): ReactNode {
  if (raw == null || raw === '') return '-'
  if (typeof raw === 'boolean') return raw ? '是' : '否'
  if (/日期|时间/.test(label)) {
    const s = String(raw).trim()
    if (!s) return '-'
    return formatDisplayDateTime(s) || s
  }
  if (/比例|税率|毛利率|应收比例/.test(label) || (/%/.test(label) && !/价税合计|不含税/.test(label))) {
    return formatScalar(raw)
  }
  if (
    /金额|价税|保证金|毛利|总额|税额|折扣额|本位币|汇率|收款|退款|已结算|成本|长短款|现金折扣|折后/.test(label) &&
    !/数量|单价|编码|行号|订单|物料|备注|方式|条件|单位|批号|计划|勾对|是否|类型|状态|名称|地点|日期|时间|理由|号码|编号|组织|部门|客户|销售员|币别|地址/.test(label)
  ) {
    if (/单价|税率|比例|折扣率|汇率/.test(label)) {
      return formatScalar(raw)
    }
    return fmtMoney(raw)
  }
  return formatScalar(raw)
}

function renderFieldGrid(
  row: Record<string, unknown>,
  fields: FieldDef[],
  cols: 2 | 3,
  extraInGrid?: ReactNode,
  plainBlackValues?: boolean,
): ReactNode {
  const gridCls = cols === 3 ? 'grid grid-cols-1 sm:grid-cols-3 gap-x-5 gap-y-2' : 'grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2'
  return (
    <div className={`pb-1 pt-1 text-[12px] ${gridCls}`}>
      {fields.map((f) => {
        // 单据状态：优先 document_status_name，其次 document_status（含金蝶码表翻译），否则 -
        const v = f.label === '单据状态' ? formatDocumentStatus(row) : pickRowValue(row, f.keys)
        const inner = normalizeDetail(
          decorateValue(f.label, formatFieldCell(f.label, v), plainBlackValues ? { plain: true } : undefined),
        )
        return (
          <DetailField key={f.label} label={f.label}>
            {inner}
          </DetailField>
        )
      })}
      {extraInGrid ?? null}
    </div>
  )
}

function buildFixedColumns(cols: TableColDef[]): ColumnsType<Record<string, unknown>> {
  return cols.map((c) => ({
    title: c.title,
    key: c.title,
    ellipsis: true,
    width: Math.min(160, 90 + c.title.length * 8),
    render: (_: unknown, record: Record<string, unknown>) => {
      const v = pickRowValue(record, c.keys)
      return normalizeDetail(formatFieldCell(c.title, v))
    },
  }))
}

function FixedLineTable({ rows, columns }: { rows: Record<string, unknown>[] | null; columns: TableColDef[] }) {
  const hasData = Array.isArray(rows) && rows.length > 0
  const dataSource = hasData ? rows! : []
  const antCols = buildFixedColumns(columns)
  return (
    <div className="pt-1 -mx-1">
      <Table<Record<string, unknown>>
        size="small"
        pagination={false}
        scroll={{ x: 'max-content' }}
        rowKey={(_, i) => `line-${i}`}
        columns={antCols}
        dataSource={dataSource}
        locale={{ emptyText: '列表内容暂无数据' }}
        className="relation-detail-subtable text-[12px] [&_.ant-table]:text-[12px] [&_.ant-table-thead>tr>th]:text-[12px] [&_.ant-table-tbody>tr>td]:text-[12px] [&_.ant-table-cell]:py-1.5"
      />
    </div>
  )
}

function LogisticsMaterialPanel({ lineRow }: { lineRow: Record<string, unknown> | null }) {
  const row = lineRow ?? {}
  return (
    <div className="space-y-6 pt-1 text-[12px]">
      {LOGISTICS_MATERIAL_GROUPS.map((grp) => (
        <div key={grp.title}>
          <div className="text-[12px] font-semibold text-gray-800 mb-2 border-b border-gray-100 pb-1">{grp.title}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3">
            {grp.fields.map((f) => {
              const v = pickRowValue(row, f.keys)
              const inner = normalizeDetail(decorateValue(f.label, formatFieldCell(f.label, v), { plain: true }))
              return (
                <DetailField key={`${grp.title}-${f.label}`} label={f.label}>
                  {inner}
                </DetailField>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

function attachmentBlock(attachments: ReturnType<typeof parseRowAttachments>, colSpan: 2 | 3) {
  return (
    <DetailField label="附件" fullWidth colSpan={colSpan}>
      {attachments.length ? (
        <ul className="list-none pl-0 m-0 space-y-2 text-[12px]">
          {attachments.map((a, i) => (
            <li key={`${a.previewHref}-${i}`} className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="text-[12px] text-black">{a.name}</span>
              <AttachmentItemLinks item={a} />
            </li>
          ))}
        </ul>
      ) : (
        <span className="text-[12px] text-black">-</span>
      )}
    </DetailField>
  )
}

export default function RelationRowDetailModal({ open, tab, row, onClose }: Props) {
  const detailKey = row && tab ? pickDetailSourceKey(row, tab) : null
  const materialKey = row && tab === 'logistics' ? pickMaterialSourceKey(row, detailKey) : null

  const detailRows = row && tab ? resolveDetailLineRows(row, tab) : null
  const planRows = row && tab === 'invoice' ? resolvePlanRows(row) : null

  const attachments = row ? parseRowAttachments(row) : []
  const firstLine = detailRows?.[0] ?? null
  const materialLineRow =
    row && tab === 'logistics' ? resolveLogisticsMaterialLineRow(row, firstLine, materialKey) : null

  const modalWidth = tab === 'contract' || tab === 'order' || tab === 'logistics' ? 1120 : 1000

  const tabItems: { key: string; label: string; children: ReactNode }[] = []

  if (row && tab === 'contract') {
    tabItems.push(
      {
        key: 'basic',
        label: '基本信息',
        children: renderFieldGrid(row, CONTRACT_BASIC_FIELDS, 3, attachmentBlock(attachments, 3), true),
      },
      {
        key: 'finance',
        label: '财务信息',
        children: renderFieldGrid(row, CONTRACT_FINANCE_FIELDS, 3, undefined, true),
      },
      {
        key: 'lines',
        label: '合同明细',
        children: (
          <div className="pt-1 -mx-1">
            <FixedLineTable rows={detailRows} columns={CONTRACT_LINE_COLUMNS} />
          </div>
        ),
      },
    )
  } else if (row && tab === 'order') {
    tabItems.push(
      {
        key: 'basic',
        label: '基本信息',
        children: renderFieldGrid(row, ORDER_BASIC_FIELDS, 3, attachmentBlock(attachments, 3), true),
      },
      {
        key: 'finance',
        label: '财务信息',
        children: renderFieldGrid(row, ORDER_FINANCE_FIELDS, 3, undefined, true),
      },
      {
        key: 'lines',
        label: '明细信息',
        children: (
          <div className="pt-1 -mx-1">
            <FixedLineTable rows={detailRows} columns={ORDER_LINE_COLUMNS} />
          </div>
        ),
      },
    )
  } else if (row && tab === 'firstPayment') {
    tabItems.push(
      {
        key: 'basic',
        label: '基本信息',
        children: renderFieldGrid(row, RECEIPT_BASIC_FIELDS, 3, attachmentBlock(attachments, 3), true),
      },
      {
        key: 'lines',
        label: '明细信息',
        children: (
          <div className="pt-1 -mx-1">
            <FixedLineTable rows={detailRows} columns={RECEIPT_LINE_COLUMNS} />
          </div>
        ),
      },
    )
  } else if (row && tab === 'logistics') {
    tabItems.push(
      {
        key: 'basic',
        label: '基本信息',
        children: renderFieldGrid(row, LOGISTICS_BASIC_FIELDS, 3, attachmentBlock(attachments, 3), true),
      },
      {
        key: 'lines',
        label: '明细信息',
        children: (
          <div className="pt-1 -mx-1">
            <FixedLineTable rows={detailRows} columns={LOGISTICS_LINE_COLUMNS} />
          </div>
        ),
      },
      {
        key: 'material',
        label: '物料数据',
        children: <LogisticsMaterialPanel lineRow={materialLineRow} />,
      },
    )
  } else if (row && tab === 'invoice') {
    tabItems.push(
      {
        key: 'basic',
        label: '基本信息',
        children: renderFieldGrid(row, INVOICE_BASIC_FIELDS, 3, attachmentBlock(attachments, 3), true),
      },
      {
        key: 'lines',
        label: '明细信息',
        children: (
          <div className="pt-1 -mx-1">
            <FixedLineTable rows={detailRows} columns={INVOICE_LINE_COLUMNS} />
          </div>
        ),
      },
      {
        key: 'plan',
        label: '收款计划',
        children: (
          <div className="pt-1 -mx-1">
            <FixedLineTable rows={planRows} columns={INVOICE_PLAN_COLUMNS} />
          </div>
        ),
      },
    )
  }

  const headline = row && tab ? headlineForRow(tab, row) : '详情'

  return (
    <Modal
      title={
        <div className="flex items-start gap-2 pr-8">
          <InfoCircleOutlined className="text-[#1677ff] mt-0.5 shrink-0" />
          <span className="font-semibold text-gray-900 leading-snug break-all">{headline}</span>
        </div>
      }
      open={open}
      onCancel={onClose}
      footer={
        <div className="flex justify-end">
          <Button type="primary" size="small" className="text-[12px]" onClick={onClose}>
            知道了
          </Button>
        </div>
      }
      width={modalWidth}
      destroyOnClose
      classNames={{ body: 'pt-2 text-[12px]' }}
    >
      {row && tab && tabItems.length ? (
        <Tabs defaultActiveKey="basic" size="small" items={tabItems} className="relation-detail-tabs text-[12px]" />
      ) : null}
    </Modal>
  )
}
